<main>
  <p-toast position="bottom-right" key="br"></p-toast>
  <loading-overlay [isLoading]="isLoading > 0"></loading-overlay>

  <header>
    <div class="headline">
      <h1>
        {{ isEditing ? ('LabDipRequest_Title' | translateWF: 'Lab Dip Request') + ' ' + labDipRequest?.requestNumber : ('LabDipRequest_New' | translateWF: 'New Lab Dip Request') }}
      </h1>
    </div>
    <div class="buttons">
      <p-button [label]="'Common_Save' | translateWF: 'Save'" [disabled]="!canEdit()" (click)="onSubmit()" />
      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="cancel()" />
    </div>
  </header>

  <!-- General Information -->
  <div class="content">
    <p-panel [header]="'LabDipRequest_General_Info' | translateWF: 'General Information'">
      <p-card>
        <div class="col">
          <div class="grid-auto-2">
            <div class="form-box">
              <label for="brandReference" class="required">{{ 'LabDipRequest_Brand' | translateWF: 'Brand' }}</label>
              <p-autocomplete
                fluid
                class="w-full"
                #brandSearch="cslServerSearch"
                [cslServerSearch]="brandTypeTypeahead"
                [suggestions]="brandSearch.results()"
                field="fieldToDisplay"
                [dropdown]="true"
                [forceSelection]="true"
                [formControl]="brandReference"
                [ngClass]="{
                  'ng-invalid ng-dirty': formGroupSubmitted && brandReference.invalid,
                }"
              /><small id="customerReference" *ngIf="brandReference.value?.code">{{ brandReference.value?.code }}</small>
            </div>
            <div class="form-box">
              <label for="customerReference" class="required">{{ 'LabDipRequest_Client' | translateWF: 'Client' }}</label>
              <p-autocomplete
                fluid
                class="w-full"
                #customerSearch="cslServerSearch"
                [cslServerSearch]="customerTypeTypeahead"
                [suggestions]="customerSearch.results()"
                field="fieldToDisplay"
                [dropdown]="true"
                [forceSelection]="true"
                [formControl]="customerReference"
                [ngClass]="{
                  'ng-invalid ng-dirty': formGroupSubmitted && customerReference.invalid,
                }"
              />
              <small id="customerReference" *ngIf="customerReference.value?.code">{{ customerReference.value?.code }}</small>
            </div>
            <div class="form-box">
              <label for="fabricCode" class="required">{{ 'LabDipRequest_Fabric' | translateWF: 'Fabric' }}</label>
              <p-autocomplete
                fluid
                class="w-full"
                #fabricSearch="cslServerSearch"
                [cslServerSearch]="fabricCategoryTypeahead"
                [suggestions]="fabricSearch.results()"
                field="fieldToDisplay"
                [forceSelection]="true"
                [dropdown]="true"
                [formControl]="fabricCodeReference"
              />

              @for (fabric of fabrics; track $index) {
                <p-tag class="mt-4">
                  ({{ fabric.fabricCode }}) {{ fabric.longDescription }}
                  <i *ngIf="canRemoveFabric(fabric.fabricCode!)" class="pi pi-times" (click)="removeFabric(fabric.fabricCode!)"></i>
                </p-tag>
              }
            </div>
            <div class="form-box">
              <label for="userNameContact" class="required">{{ 'LabDipRequest_Created_By' | translateWF: 'Created By' }}</label>
              <input type="text" [readOnly]="true" [disabled]="true" [value]="createdByDisplayName || (labDipRequest?.createdBy ?? user?.firstName)" pInputText class="w-full" />
            </div>
            <div class="form-box">
              <label for="illumination" class="required">{{ 'LabDipRequest_Illumination' | translateWF: 'Illumination' }}</label>
              <p-select class="w-full" [options]="illuminationTypesOptions" [formControl]="illumination" />
            </div>
          </div>
          <div class="row-start-center">
            <div class="flex align-items-center">
              <p-checkbox [formControl]="isForProductDevelopment" [binary]="true" inputId="isForProductDevelopment" />
              <label for="isForProductDevelopment" class="ml-2">
                {{ 'LabDipRequest_IsForProductDevelopment' | translateWF: 'For Product Development' }}
              </label>
            </div>
          </div>
        </div>
      </p-card>
    </p-panel>

    <p-panel [header]="'LabDipRequest_Colors' | translateWF: 'Colors'">
      <ng-template pTemplate="icons">
        @if (canEdit()) {
          <p-button [label]="'LabDipRequest_Add_Color' | translateWF: 'Add Color'" icon="pi pi-plus" variant="outlined" [disabled]="!canEdit()" (click)="addColor()" />
        }
      </ng-template>

      <p-card data-variant="compact">
        <div class="grid-1 gap-[1rem]">
          <div>
            <form [formGroup]="formGroup" class="table-form">
              <p-table [value]="colors.controls" formArrayName="colors" appendTo="body" [scrollable]="false">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'LabDipRequest_Color' | translateWF: 'Color' }}</th>
                    <th>{{ 'LabDipRequest_Status' | translateWF: 'Status' }}</th>
                    <th>
                      {{ 'LabDipRequest_Dye_Type' | translateWF: 'Dye Type' }}
                    </th>
                    <th>
                      {{ 'LabDipRequest_Standard' | translateWF: 'Standard' }}
                    </th>
                    <th>
                      {{ 'LabDipRequest_Old_Lab_Dip' | translateWF: 'Old Lab Dip' }}
                    </th>
                    <th>
                      {{ 'LabDipRequest_Approved' | translateWF: 'Approved Lab Dip' }}
                    </th>
                    <th>
                      {{ 'LabDipRequest_Actions' | translateWF: 'Actions' }}
                    </th>
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-color let-i="rowIndex">
                  <tr [formGroupName]="i">
                    <td>
                      <input
                        pInputText
                        type="text"
                        formControlName="name"
                        class="max-w-[130px]"
                        [ngClass]="{
                          'ng-invalid ng-dirty': (formGroupSubmitted || color.get('name')?.touched) && color.get('name')?.invalid,
                          'p-invalid': (formGroupSubmitted || color.get('name')?.touched) && color.get('name')?.invalid,
                        }"
                      />
                      <p class="error" *ngIf="color.get('name')?.errors?.['duplicateColor']" class="error">
                        {{ color.get('name')?.errors?.['duplicateColor'] }}
                      </p>
                      @if (getExistingColorWarning(color); as warning) {
                        <p-message severity="warn" styleClass="mt-1" [text]="warning" />
                      }
                    </td>
                    <td>
                      <p-select appendTo="body" class="min-w-[130px]" [options]="colorStatusOptions" formControlName="status" />
                    </td>
                    <td>
                      <p-select appendTo="body" class="min-w-[130px]" [options]="dyeingTypesOptions" formControlName="dyeingType" />
                    </td>
                    <td>
                      <div class="col-sm">
                        <csl-dropdown-master-data formControlName="dyeingColorStandardCode" categoryCode="DyeingColorStandard" [allowCreate]="false" />
                        <!-- Conditional Pantone field -->
                        <input
                          *ngIf="color.get('dyeingColorStandardCode')?.value === '6'"
                          pInputText
                          type="text"
                          formControlName="pantone"
                          placeholder="Pantone Code"
                          class="min-w-[180px]"
                        />
                        <!-- Conditional Sample Recipe Number field -->
                        <input
                          *ngIf="color.get('dyeingColorStandardCode')?.value === '7'"
                          pInputText
                          type="text"
                          formControlName="sampleRecipeNumber"
                          placeholder="Recipe Number"
                          class="min-w-[180px]"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="col-sm">
                        <div class="flex align-items-center">
                          <p-checkbox formControlName="isOldLabDip" [binary]="true" inputId="isOldLabDip_{{ i }}" />
                          <label for="isOldLabDip_{{ i }}" class="ml-2">
                            {{ 'LabDipRequest_Is_Old_Lab_Dip' | translateWF: 'Is Old Lab Dip' }}
                          </label>
                        </div>
                        <div *ngIf="color.get('isOldLabDip')?.value" class="col-sm">
                          <input
                            pInputText
                            type="text"
                            formControlName="customLabDipNumber"
                            placeholder="{{ 'LabDipRequest_Custom_Lab_Dip_Placeholder' | translateWF: 'e.g., LD-20251000001' }}"
                            class="min-w-[180px]"
                            [ngClass]="{ 'ng-invalid ng-dirty': color.get('customLabDipNumber')?.invalid && color.get('customLabDipNumber')?.touched }"
                          />
                          <small *ngIf="color.get('customLabDipNumber')?.invalid && color.get('customLabDipNumber')?.touched" class="p-error">
                            {{ color.get('customLabDipNumber')?.errors?.['invalidLabDipFormat'] }}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {{ color.get('approvedRecipeNumber')?.value }}
                    </td>
                    <td>
                      <div class="flex gap-[0.5rem]">
                        <p-button icon="pi pi-trash" [disabled]="!canEdit()" (click)="removeColor(i)" />
                        <p-button icon="pi pi-pencil" [disabled]="!canEdit()" (click)="openColorLabDipsManagementModal(i)" />
                      </div>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </form>
          </div>
        </div>
      </p-card>
    </p-panel>
  </div>
</main>
